import React, { useState, useEffect } from "react";
import { FaPlus, FaSearch, FaUser } from "react-icons/fa";
import { Checkbox } from "../../shadcn/ui/checkbox";
import billingService from "../../services/billingService";
import { organizationApiService } from "../../services/apiV2Service";
import { getCurrentUserOrganizationId } from "../host-experience/host-clients/utils/hostClientsUtils";
import moment from "moment";

import ReusableFormModal from "../../components/ReusableFormModal";
import { Tabs, TabsList, TabsTrigger } from "../../shadcn/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../shadcn/ui/table";
import logger from "../../utils/logger";
import { showApiMessage } from "../../utils/messageHandler";
import HConfirmDialog from "../../hcomponents/HConfirmDialog";
import IconWordResponsiveButton from "../../components/IconWordResponsiveButton";
import SuccessAlert from "../settings/components/alerts/SuccessAlert";
import Pagination from "../../components/Pagination";
import TablePageLayout from "../../components/TablePageLayout";

const OffersDiscounts = () => {
  // State for discounts
  const [discounts, setDiscounts] = useState([]);
  const [discountCurrentPage, setDiscountCurrentPage] = useState(1);
  const [discountTotalCount, setDiscountTotalCount] = useState(0);
  const [discountPageSize] = useState(10);
  const [newDiscount, setNewDiscount] = useState({
    code: "",
    name: "",
    description: "",
    active: true,
    percentage: null,
    fixedValue: null,
    startDate: "",
    endDate: "",
    organizationId: null, // Will be set based on provider selection
  });

  // State for offers
  const [offers, setOffers] = useState([]);
  const [offerCurrentPage, setOfferCurrentPage] = useState(1);
  const [offerTotalCount, setOfferTotalCount] = useState(0);
  const [offerPageSize] = useState(10);
  const [newOffer, setNewOffer] = useState({
    code: "",
    name: "",
    description: "",
    organizationId: null, // Will be set based on provider selection
    providerId: null, // Will be set based on provider selection
    serviceSubcategoryId: null,
    durationUnit: "HOUR",
    minimumDuration: null,
    maximumDuration: null,
    maximumCheckIns: null,
    numberOfServiceProviders: 1,
    serviceTier: "HIGH",
    currencyId: null, // REQUIRED - price currency
    unitPrice: 0, // REQUIRED - price amount
    packageIds: [],
  });

  // Common state
  const [activeTab, setActiveTab] = useState("discounts");
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'grid'
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [offerFormErrors, setOfferFormErrors] = useState({});
  const [showCreateDiscountModal, setShowCreateDiscountModal] = useState(false);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOfferDetails, setIsLoadingOfferDetails] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packageCurrentPage, setPackageCurrentPage] = useState(1);
  const [packagePageSize] = useState(50);
  const [packageTotalCount, setPackageTotalCount] = useState(0);
  // Currencies state
  const [currencies, setCurrencies] = useState([]);
  const [currencyCurrentPage, setCurrencyCurrentPage] = useState(1);
  const [currencyTotalCount, setCurrencyTotalCount] = useState(0);
  const [currencyPageSize] = useState(10);
  const [newCurrency, setNewCurrency] = useState({
    code: "",
    name: "",
    symbol: "",
    active: true,
    isDefault: false,
  });
  const [editingCurrency, setEditingCurrency] = useState(null);

  // Provider selection state
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceSubcategories, setServiceSubcategories] = useState([]);
  const [_selectedCategoryId, setSelectedCategoryId] = useState(null);

  // Organization search state
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Confirm dialog states
  const [showDeleteDiscountDialog, setShowDeleteDiscountDialog] = useState(false);
  const [showDeleteOfferDialog, setShowDeleteOfferDialog] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [showCreateCurrencyModal, setShowCreateCurrencyModal] = useState(false);
  const [showDeleteCurrencyDialog, setShowDeleteCurrencyDialog] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);

  // Initial load - fetch non-paginated data
  useEffect(() => {
    fetchPackages();
    fetchServiceCategories();
  }, []);

  // Reload packages when provider changes (for offer modal)
  useEffect(() => {
    if (showCreateOfferModal) {
      setPackageCurrentPage(1);
      fetchPackages(1);
    }
  }, [selectedProviderId, showCreateOfferModal]);

  // Fetch discounts when page changes
  useEffect(() => {
    fetchDiscounts();
  }, [discountCurrentPage]);

  // Fetch offers when page changes
  useEffect(() => {
    fetchOffers();
  }, [offerCurrentPage]);

  // Fetch currencies when page changes
  useEffect(() => {
    fetchCurrencies();
  }, [currencyCurrentPage]);

  const fetchServiceCategories = async () => {
    try {
      logger.log("Fetching service categories...");
      const response = await billingService.getServiceCategories();
      logger.log("Categories response:", response);

      if (response.data && Array.isArray(response.data)) {
        logger.log("Setting categories from response.data:", response.data);
        setServiceCategories(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        logger.log("Setting categories from response.data.data:", response.data.data);
        setServiceCategories(response.data.data);
      } else {
        logger.log("No valid categories data found in response");
        setServiceCategories([]);
      }
    } catch (error) {
      logger.error("Error fetching service categories:", error);
      logger.error("Error fetching service categories:", error);
    }
  };

  const fetchDiscounts = async () => {
    try {
      setIsLoading(true);
      const response = await billingService.getDiscounts({
        page: discountCurrentPage,
        limit: discountPageSize,
      });
      if (response.data) {
        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setDiscounts(data);

        // Extract pagination info safely
        const pagination = response.data.pagination || {};
        const totalCount = parseInt(pagination.totalCount || pagination.total || data.length || 0, 10);
        setDiscountTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error fetching discounts:", error);
      showApiMessage(error, "fetch discounts");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOffers = async () => {
    try {
      setIsLoading(true);
      const response = await billingService.getOffers({
        page: offerCurrentPage,
        limit: offerPageSize,
        type: "BUNDLE",
      });
      if (response.data) {
        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setOffers(data);

        // Extract pagination info safely
        const pagination = response.data.pagination || {};
        const totalCount = parseInt(pagination.totalCount || pagination.total || data.length || 0, 10);
        setOfferTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error fetching offers:", error);
      showApiMessage(error, "fetch offers");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPackages = async (page = 1) => {
    try {
      const params = selectedProviderId ? { providerId: selectedProviderId } : {};
      params.page = page;
      params.limit = packagePageSize;
      params.type = "BUNDLE";
      const response = await billingService.getPackages(params);
      if (response.data) {
        const pkgs = Array.isArray(response.data.data) ? response.data.data : [];
        setPackages(pkgs);

        // Extract pagination info
        const pagination = response.data.pagination || {};
        const totalCount = parseInt(pagination.totalCount || pagination.total || pkgs.length || 0, 10);
        setPackageTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error fetching packages:", error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      setIsLoading(true);
      const response = await billingService.getCurrencies({
        page: currencyCurrentPage,
        limit: currencyPageSize,
      });
      if (response.data) {
        const list = Array.isArray(response.data.data) ? response.data.data : response.data;
        const data = Array.isArray(list) ? list : [];
        setCurrencies(data);

        // Extract pagination info safely
        const pagination = response.data.pagination || {};
        const totalCount = parseInt(pagination.totalCount || pagination.total || data.length || 0, 10);
        setCurrencyTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error fetching currencies:", error);
      showApiMessage(error, "fetch currencies");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchServiceSubcategories = async categoryId => {
    try {
      logger.log("Fetching service subcategories for categoryId:", categoryId);
      const response = await billingService.getServiceSubcategories(categoryId);
      logger.log("Subcategories response:", response);

      if (response.data && Array.isArray(response.data)) {
        logger.log("Setting subcategories from response.data:", response.data);
        setServiceSubcategories(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        logger.log("Setting subcategories from response.data.data:", response.data.data);
        setServiceSubcategories(response.data.data);
      } else {
        logger.log("No valid subcategories data found in response");
        setServiceSubcategories([]);
      }
    } catch (error) {
      logger.error("Error fetching service subcategories:", error);
      logger.error("Error fetching service subcategories:", error);
    }
  };

  const handleDiscountInputChange = (field, value) => {
    setNewDiscount(prev => ({ ...prev, [field]: value }));
  };

  const handleOfferInputChange = (field, value) => {
    setNewOffer(prev => ({ ...prev, [field]: value }));
    // Clear form errors when user starts typing/selecting
    if (offerFormErrors[field]) {
      setOfferFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const searchOrganizations = async searchTerm => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const currentOrgId = getCurrentUserOrganizationId();
      const response = await organizationApiService.searchOrganizations({
        q: searchTerm,
        limit: 10,
      });

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Filter out current user's organization in UI
        const filteredResults = response.data.data.filter(org => org.id !== currentOrgId);
        setSearchResults(filteredResults);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      logger.error("Error searching organizations:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleOrganizationSelection = organization => {
    setSelectedOrganization(organization);
    setSelectedProviderId(organization.id);

    // Update organizationId and providerId in forms based on selected organization
    setNewDiscount(prev => ({ ...prev, organizationId: organization.id }));
    setNewOffer(prev => ({ ...prev, organizationId: organization.id, providerId: organization.id }));

    // Organization selection is optional, no error clearing needed

    // Clear search
    setOrganizationSearchTerm("");
    setSearchResults([]);
  };

  const handleCreateDiscount = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Convert percentage to decimal (e.g., 20% -> 0.2)
      const discountPayload = {
        ...newDiscount,
        percentage: newDiscount.percentage ? newDiscount.percentage / 100 : undefined,
      };

      const resp = await billingService.createDiscount(discountPayload);
      showApiMessage(resp, "create discount");
      setShowCreateDiscountModal(false);
      setDiscountCurrentPage(1); // Reset to first page
      fetchDiscounts();
      resetDiscountForm();
    } catch (error) {
      logger.error("Error creating discount:", error);
      showApiMessage(error, "create discount");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOffer = async e => {
    e.preventDefault();
    logger.log("🚀 handleCreateOffer called");
    logger.log("🚀 Current newOffer state:", newOffer);

    // Clear previous form errors
    setOfferFormErrors({});

    // Validate form fields
    const errors = {};
    // organizationId is optional - if not provided, offer will be unlinked
    if (!newOffer.serviceSubcategoryId) {
      errors.serviceSubcategoryId = "Please select a service subcategory for this offer";
    }
    if (!newOffer.currencyId) {
      errors.currencyId = "Please select a currency for this offer";
    }
    if (!newOffer.unitPrice || newOffer.unitPrice <= 0) {
      errors.unitPrice = "Please enter a valid unit price greater than 0";
    }
    if (!newOffer.maximumCheckIns || newOffer.maximumCheckIns <= 0) {
      errors.maximumCheckIns = "Please enter a valid maximum check-ins value";
    }

    logger.log("🚀 Validation errors:", errors);

    // If there are validation errors, show them and don't submit
    if (Object.keys(errors).length > 0) {
      logger.log("🚀 Validation failed, showing errors");
      setOfferFormErrors(errors);
      return;
    }

    logger.log("🚀 Validation passed, proceeding with API call");

    try {
      setIsLoading(true);

      const offerPayload = {
        ...newOffer,
        providerId: newOffer.providerId && newOffer.providerId !== 0 ? newOffer.providerId : null,
      };

      logger.log("Creating offer with payload:", offerPayload);

      const resp = await billingService.createOffer(offerPayload);
      showApiMessage(resp, "create offer");
      setShowCreateOfferModal(false);
      setOfferCurrentPage(1); // Reset to first page
      fetchOffers();
      resetOfferForm();
    } catch (error) {
      logger.error("Error creating offer:", error);
      logger.error("Error creating offer:", error);
      showApiMessage(error, "create offer");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCurrency = currency => {
    setEditingCurrency(currency);
    setNewCurrency({
      code: currency.code || "",
      name: currency.name || "",
      symbol: currency.symbol || "",
      active: !!currency.active,
      isDefault: !!currency.isDefault,
    });
    setShowCreateCurrencyModal(true);
  };

  const handleUpdateCurrency = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const payload = {
        code: newCurrency.code,
        name: newCurrency.name,
        symbol: newCurrency.symbol,
        active: newCurrency.active,
        isDefault: newCurrency.isDefault,
      };
      const resp = await billingService.updateCurrency(editingCurrency.id, payload);
      showApiMessage(resp, "update currency");
      setShowCreateCurrencyModal(false);
      setEditingCurrency(null);
      fetchCurrencies();
    } catch (error) {
      logger.error("Error updating currency:", error);
      showApiMessage(error, "update currency");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCurrency = currency => {
    setCurrencyToDelete(currency);
    setShowDeleteCurrencyDialog(true);
  };

  const confirmDeleteCurrency = async () => {
    if (!currencyToDelete) return;
    try {
      setIsLoading(true);
      const resp = await billingService.deleteCurrency(currencyToDelete.id);
      showApiMessage(resp, "delete currency");
      // If current page becomes empty, go to previous page
      if (currencies.length === 1 && currencyCurrentPage > 1) {
        setCurrencyCurrentPage(currencyCurrentPage - 1);
      } else {
        fetchCurrencies();
      }
    } catch (error) {
      logger.error("Error deleting currency:", error);
      showApiMessage(error, "delete currency");
    } finally {
      setIsLoading(false);
      setShowDeleteCurrencyDialog(false);
      setCurrencyToDelete(null);
    }
  };

  const handleEditDiscount = discount => {
    setEditingDiscount(discount);
    setNewDiscount({
      ...discount,
      // Convert decimal percentage back to whole number for form display
      percentage: discount.percentage ? Math.round(discount.percentage * 100) : undefined,
      startDate: discount.startDate ? moment(discount.startDate).format("YYYY-MM-DD") : "",
      endDate: discount.endDate ? moment(discount.endDate).format("YYYY-MM-DD") : "",
      organizationId: discount.organizationId,
    });
    setSelectedProviderId(discount.organizationId);
    setShowCreateDiscountModal(true);
  };

  const handleEditOffer = async offer => {
    try {
      logger.log("Editing offer:", offer);

      // Show loading state and open modal immediately
      setIsLoadingOfferDetails(true);
      setShowCreateOfferModal(true);

      // Fetch complete offer details from API
      const response = await billingService.getOfferById(offer.id);
      const fullOffer = response.data?.data || offer;

      logger.log("Full offer data from API:", fullOffer);
      logger.log("Offer packages:", fullOffer.packages);
      logger.log("Offer organization:", fullOffer.organization);
      logger.log("Offer serviceSubcategory:", fullOffer.serviceSubcategory);

      // Extract package IDs from the packages array if it exists
      let packageIds = [];
      if (fullOffer.packages && Array.isArray(fullOffer.packages)) {
        packageIds = fullOffer.packages.map(pkg => pkg.id);
        logger.log("Extracted package IDs from packages array:", packageIds);
      } else if (fullOffer.packageIds && Array.isArray(fullOffer.packageIds)) {
        packageIds = fullOffer.packageIds;
        logger.log("Using existing packageIds:", packageIds);
      }

      setEditingOffer(fullOffer);
      setNewOffer({
        ...fullOffer,
        packageIds: packageIds,
        // keep providerId for PATCH; do not persist organizationId in form state
        providerId: fullOffer.providerId ?? fullOffer.organizationId ?? null,
      });

      // Set the selected provider ID (prefer providerId)
      setSelectedProviderId(fullOffer.providerId ?? fullOffer.organizationId ?? null);

      // If provider organization is present, set it for display
      if (fullOffer.provider || fullOffer.organization) {
        setSelectedOrganization(fullOffer.provider || fullOffer.organization);
      } else {
        setSelectedOrganization(null);
      }

      // Set the selected category based on the offer's subcategory
      if (fullOffer.serviceSubcategoryId) {
        // First try to find in existing subcategories
        let subcategory = serviceSubcategories.find(sub => sub.id === fullOffer.serviceSubcategoryId);

        if (subcategory) {
          setSelectedCategoryId(subcategory.categoryId);
          fetchServiceSubcategories(subcategory.categoryId);
        } else {
          // If not found, we need to fetch subcategories for all categories to find the right one
          logger.log("Subcategory not found in existing data, fetching all subcategories...");
          Promise.all(
            serviceCategories.map(cat =>
              billingService
                .getServiceSubcategories(cat.id)
                .then(response => {
                  const subs =
                    response.data && Array.isArray(response.data)
                      ? response.data
                      : response.data && response.data.data && Array.isArray(response.data.data)
                        ? response.data.data
                        : [];
                  return { categoryId: cat.id, subcategories: subs };
                })
                .catch(() => ({ categoryId: cat.id, subcategories: [] }))
            )
          ).then(results => {
            for (const result of results) {
              const foundSub = result.subcategories.find(sub => sub.id === fullOffer.serviceSubcategoryId);
              if (foundSub) {
                logger.log("Found subcategory in category:", result.categoryId);
                setSelectedCategoryId(result.categoryId);
                setServiceSubcategories(result.subcategories);
                break;
              }
            }
          });
        }
      } else {
        // Clear category selection if no subcategory
        setSelectedCategoryId(null);
        setServiceSubcategories([]);
      }

      // Hide loading state after data is loaded
      setIsLoadingOfferDetails(false);
    } catch (error) {
      logger.error("Error fetching offer details:", error);
      // Fallback to using the offer data from the table
      setEditingOffer(offer);
      setNewOffer({
        ...offer,
        packageIds: offer.packageIds || [],
        organizationId: offer.organizationId,
      });
      setIsLoadingOfferDetails(false);
    }
  };

  const resetDiscountForm = () => {
    setNewDiscount({
      code: "",
      name: "",
      description: "",
      active: true,
      percentage: null,
      fixedValue: null,
      startDate: "",
      endDate: "",
      organizationId: null,
    });
    setSelectedProviderId(null);
    setSelectedOrganization(null);
    setOrganizationSearchTerm("");
    setSearchResults([]);
  };

  const resetOfferForm = () => {
    setNewOffer({
      code: "",
      name: "",
      description: "",
      organizationId: null,
      providerId: null,
      serviceSubcategoryId: null,
      durationUnit: "HOUR",
      minimumDuration: null,
      maximumDuration: null,
      maximumCheckIns: null,
      numberOfServiceProviders: 1,
      serviceTier: "HIGH",
      currencyId: null, // REQUIRED - price currency
      unitPrice: 0, // REQUIRED - price amount
      packageIds: [],
    });
    setSelectedProviderId(null);
    setSelectedCategoryId(null);
    setServiceSubcategories([]);
    setSelectedOrganization(null);
    setOrganizationSearchTerm("");
    setSearchResults([]);
  };

  const handleUpdateDiscount = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Only send the fields that are allowed for updates
      const discountPayload = {
        code: newDiscount.code,
        name: newDiscount.name,
        description: newDiscount.description,
        percentage: newDiscount.percentage ? newDiscount.percentage / 100 : undefined,
        fixedValue: newDiscount.fixedValue,
        startDate: newDiscount.startDate,
        endDate: newDiscount.endDate,
        active: newDiscount.active,
      };

      logger.log("Update discount payload:", discountPayload);

      const resp = await billingService.updateDiscount(editingDiscount.id, discountPayload);
      showApiMessage(resp, "update discount");
      setShowCreateDiscountModal(false);
      setEditingDiscount(null);
      fetchDiscounts(); // Keep current page
      resetDiscountForm();
    } catch (error) {
      logger.error("Error updating discount:", error);
      logger.error("Error updating discount:", error);
      showApiMessage(error, "update discount");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateOffer = async e => {
    e.preventDefault();

    // Clear previous form errors
    setOfferFormErrors({});

    // Validate form fields
    const errors = {};
    // organizationId is optional - if not provided, offer will be unlinked
    if (!newOffer.serviceSubcategoryId) {
      errors.serviceSubcategoryId = "Please select a service subcategory for this offer";
    }
    if (!newOffer.currencyId) {
      errors.currencyId = "Please select a currency for this offer";
    }
    if (!newOffer.unitPrice || newOffer.unitPrice <= 0) {
      errors.unitPrice = "Please enter a valid unit price greater than 0";
    }
    if (!newOffer.maximumCheckIns || newOffer.maximumCheckIns <= 0) {
      errors.maximumCheckIns = "Please enter a valid maximum check-ins value";
    }

    // If there are validation errors, show them and don't submit
    if (Object.keys(errors).length > 0) {
      setOfferFormErrors(errors);
      return;
    }

    try {
      setIsLoading(true);

      // Only send the fields that are allowed for updates
      const offerPayload = {
        code: newOffer.code,
        name: newOffer.name,
        description: newOffer.description,
        serviceSubcategoryId: newOffer.serviceSubcategoryId,
        durationUnit: newOffer.durationUnit,
        minimumDuration: newOffer.minimumDuration,
        maximumDuration: newOffer.maximumDuration,
        maximumCheckIns: newOffer.maximumCheckIns,
        numberOfServiceProviders: newOffer.numberOfServiceProviders,
        serviceTier: newOffer.serviceTier,
        currencyId: newOffer.currencyId, // Price field
        unitPrice: newOffer.unitPrice, // Price field
        // Do NOT send organizationId in PATCH; only providerId is allowed
        providerId: newOffer.providerId && newOffer.providerId !== 0 ? newOffer.providerId : null,
      };

      // Handle package associations using the correct API fields
      if (editingOffer && editingOffer.packageIds) {
        // Get the original package IDs from the offer being edited
        const originalPackageIds = editingOffer.packageIds || [];
        const newPackageIds = newOffer.packageIds || [];

        // Find packages to link (new ones that weren't in the original)
        const linkedPackageIds = newPackageIds.filter(id => !originalPackageIds.includes(id));

        // Find packages to unlink (original ones that aren't in the new selection)
        const unlinkedPackageIds = originalPackageIds.filter(id => !newPackageIds.includes(id));

        if (linkedPackageIds.length > 0) {
          offerPayload.linkedPackageIds = linkedPackageIds;
        }

        if (unlinkedPackageIds.length > 0) {
          offerPayload.unlinkedPackageIds = unlinkedPackageIds;
        }
      } else if (newOffer.packageIds && newOffer.packageIds.length > 0) {
        // If this is a new offer or no original packages, just link the selected packages
        offerPayload.linkedPackageIds = newOffer.packageIds;
      }

      logger.log("Update offer payload:", offerPayload);

      const resp = await billingService.updateOffer(editingOffer.id, offerPayload);
      showApiMessage(resp, "update offer");
      setShowCreateOfferModal(false);
      setEditingOffer(null);
      fetchOffers(); // Keep current page
      resetOfferForm();
    } catch (error) {
      logger.error("Error updating offer:", error);
      logger.error("Error updating offer:", error);
      showApiMessage(error, "update offer");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDiscount = async discountId => {
    const discount = discounts.find(d => d.id === discountId);
    setDiscountToDelete(discount);
    setShowDeleteDiscountDialog(true);
  };

  const handleDeleteOffer = async offerId => {
    const offer = offers.find(o => o.id === offerId);
    setOfferToDelete(offer);
    setShowDeleteOfferDialog(true);
  };

  const handleCreateCurrency = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const payload = {
        code: newCurrency.code,
        name: newCurrency.name,
        symbol: newCurrency.symbol,
        active: newCurrency.active,
        isDefault: newCurrency.isDefault,
      };
      await billingService.createCurrency(payload);
      setSuccessMessage("successfully created currency");
      setShowSuccessAlert(true);
      setShowCreateCurrencyModal(false);
      setNewCurrency({
        code: "",
        name: "",
        symbol: "",
        active: true,
        isDefault: false,
      });
      setCurrencyCurrentPage(1); // Reset to first page
      fetchCurrencies();
    } catch (error) {
      logger.error("Error creating currency:", error);
      showApiMessage(error, "create currency");
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeleteDiscount = async () => {
    if (!discountToDelete) return;

    try {
      setIsLoading(true);
      const resp = await billingService.deleteDiscount(discountToDelete.id);
      showApiMessage(resp, "delete discount");
      // If current page becomes empty, go to previous page
      if (discounts.length === 1 && discountCurrentPage > 1) {
        setDiscountCurrentPage(discountCurrentPage - 1);
      } else {
        fetchDiscounts();
      }
    } catch (error) {
      logger.error("Error deleting discount:", error);
      showApiMessage(error, "delete discount");
    } finally {
      setIsLoading(false);
      setShowDeleteDiscountDialog(false);
      setDiscountToDelete(null);
    }
  };

  const confirmDeleteOffer = async () => {
    if (!offerToDelete) return;

    try {
      setIsLoading(true);
      const resp = await billingService.deleteOffer(offerToDelete.id);
      showApiMessage(resp, "delete offer");
      // If current page becomes empty, go to previous page
      if (offers.length === 1 && offerCurrentPage > 1) {
        setOfferCurrentPage(offerCurrentPage - 1);
      } else {
        fetchOffers();
      }
    } catch (error) {
      logger.error("Error deleting offer:", error);
      showApiMessage(error, "delete offer");
    } finally {
      setIsLoading(false);
      setShowDeleteOfferDialog(false);
      setOfferToDelete(null);
    }
  };

  const formatDate = dateString => {
    return dateString ? moment(dateString).format("MMM DD, YYYY") : "N/A";
  };

  // Render table with columns and data
  const renderTable = (data, columns, emptyMessage, loading) => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">{emptyMessage}</div>
        </div>
      );
    }

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column, index) => (
                <TableHead key={index} className="text-left">
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex}>
                    {column.render ? column.render(item) : item[column.key] || "N/A"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Table columns for discounts
  const discountColumns = [
    {
      key: "code",
      header: "Code",
      render: item => <div className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">{item.code}</div>,
      mobileLabel: "Code",
    },
    {
      key: "name",
      header: "Name",
      render: item => (
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-sm text-gray-500 truncate max-w-[200px]">{item.description}</div>
        </div>
      ),
      mobileLabel: "Name",
    },
    {
      key: "discount",
      header: "Discount",
      render: item => (
        <div className="font-bold text-green-600">
          {item.percentage ? `${(item.percentage * 100).toFixed(0)}%` : `$${item.fixedValue}`}
        </div>
      ),
      mobileLabel: "Discount",
    },
    {
      key: "validity",
      header: "Validity Period",
      render: item => (
        <div className="text-sm">
          <div>{formatDate(item.startDate)}</div>
          <div className="text-gray-500">to {formatDate(item.endDate)}</div>
        </div>
      ),
      mobileLabel: "Validity",
    },
    {
      key: "active",
      header: "Status",
      render: item => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {item.active ? "Active" : "Inactive"}
        </span>
      ),
      mobileLabel: "Status",
    },
    {
      key: "actions",
      header: "Actions",
      render: item => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditDiscount(item)}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteDiscount(item.id)}
            className="p-1 text-red-600 hover:text-red-800"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
      mobileLabel: "Actions",
    },
  ];

  // Table columns for offers
  const offerColumns = [
    {
      key: "code",
      header: "Code",
      render: item => <div className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">{item.code}</div>,
      mobileLabel: "Code",
    },
    {
      key: "name",
      header: "Name",
      render: item => (
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-sm text-gray-500 truncate max-w-[200px]">{item.description}</div>
        </div>
      ),
      mobileLabel: "Name",
    },
    {
      key: "duration",
      header: "Duration",
      render: item => (
        <div className="text-sm">
          <div>
            {item.minimumDuration}-{item.maximumDuration} {item.durationUnit}S
          </div>
        </div>
      ),
      mobileLabel: "Duration",
    },
    {
      key: "serviceTier",
      header: "Service Tier",
      render: item => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.serviceTier === "HIGH"
              ? "bg-purple-100 text-purple-800"
              : item.serviceTier === "MEDIUM"
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-800"
          }`}
        >
          {item.serviceTier}
        </span>
      ),
      mobileLabel: "Service Tier",
    },
    {
      key: "providers",
      header: "Providers",
      render: item => (
        <div className="text-center">
          <div className="text-lg font-bold">{item.numberOfServiceProviders}</div>
          <div className="text-xs text-gray-500">providers</div>
        </div>
      ),
      mobileLabel: "Providers",
    },
    {
      key: "actions",
      header: "Actions",
      render: item => (
        <div className="flex space-x-2">
          <button onClick={() => handleEditOffer(item)} className="p-1 text-blue-600 hover:text-blue-800" title="Edit">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteOffer(item.id)}
            className="p-1 text-red-600 hover:text-red-800"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
      mobileLabel: "Actions",
    },
  ];

  return (
    <TablePageLayout
      title="Offers & Discounts"
      subtitle="Manage your discounts, offers, and currencies"
      viewMode={activeTab === "currencies" ? undefined : viewMode}
      onViewModeChange={setViewMode}
      viewOptions={["list", "grid"]}
      bottomContent={
        <div className="pt-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100 p-1 rounded-full inline-flex">
              <TabsTrigger
                value="discounts"
                className="rounded-full px-6 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Discounts
              </TabsTrigger>
              <TabsTrigger
                value="offers"
                className="rounded-full px-6 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Offers
              </TabsTrigger>
              <TabsTrigger
                value="currencies"
                className="rounded-full px-6 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Currencies
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      }
    >
      <SuccessAlert
        show={showSuccessAlert}
        message={successMessage || "operation successful"}
        close={() => setShowSuccessAlert(false)}
      />

      <div className="space-y-6">
        {/* Discounts Tab */}
        {activeTab === "discounts" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h5 className="text-lg font-semibold">Discount Management</h5>
              <IconWordResponsiveButton
                icon={<FaPlus />}
                text="Create Discount"
                onClick={() => {
                  setEditingDiscount(null);
                  resetDiscountForm();
                  setShowCreateDiscountModal(true);
                }}
              />
            </div>

            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-xl border border-gray-100 p-5 animate-pulse bg-white">
                      <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
                      <div className="h-3 w-32 bg-gray-200 rounded mb-2" />
                      <div className="h-6 w-16 bg-gray-200 rounded" />
                    </div>
                  ))
                ) : discounts.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-gray-500">No discounts found</div>
                ) : (
                  discounts.map((item, i) => (
                    <div
                      key={item.id || i}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col gap-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-mono text-xs bg-gray-100 rounded px-2 py-1">{item.code}</div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${item.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                        >
                          {item.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-500 line-clamp-2">{item.description}</div>
                      </div>
                      <div className="text-xl font-bold text-green-600">
                        {item.percentage ? `${(item.percentage * 100).toFixed(0)}%` : `$${item.fixedValue}`}
                      </div>
                      {(item.startDate || item.endDate) && (
                        <div className="text-xs text-gray-500">
                          {formatDate(item.startDate)} → {formatDate(item.endDate)}
                        </div>
                      )}
                      <div className="border-t border-gray-100" />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditDiscount(item)}
                          className="flex-1 text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDiscount(item.id)}
                          className="text-xs px-3 py-1.5 rounded-md border border-red-200 hover:bg-red-50 text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              renderTable(discounts, discountColumns, "No discounts found", isLoading)
            )}

            {/* Pagination for Discounts */}
            {discountTotalCount > 0 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  className="pagination-bar"
                  currentPage={discountCurrentPage}
                  totalCount={discountTotalCount}
                  pageSize={discountPageSize}
                  siblingCount={1}
                  onPageChange={({ selected }) => {
                    const page = parseInt(selected, 10);
                    if (!isNaN(page) && page > 0) {
                      setDiscountCurrentPage(page);
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === "offers" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h5 className="text-lg font-semibold">Offer Management</h5>
              <IconWordResponsiveButton
                icon={<FaPlus />}
                text="Create Offer"
                onClick={() => {
                  setEditingOffer(null);
                  resetOfferForm();
                  setShowCreateOfferModal(true);
                }}
              />
            </div>

            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-xl border border-gray-100 p-5 animate-pulse bg-white">
                      <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
                      <div className="h-3 w-32 bg-gray-200 rounded mb-2" />
                      <div className="h-6 w-16 bg-gray-200 rounded" />
                    </div>
                  ))
                ) : offers.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-gray-500">No offers found</div>
                ) : (
                  offers.map((item, i) => (
                    <div
                      key={item.id || i}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col gap-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-mono text-xs bg-gray-100 rounded px-2 py-1">{item.code}</div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>
                          {item.serviceTier}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-500 line-clamp-2">{item.description}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div>
                          <span className="text-gray-400">Duration:</span> {item.minimumDuration} {item.durationUnit}
                        </div>
                        <div>
                          <span className="text-gray-400">Check-ins:</span> {item.maximumCheckIns}
                        </div>
                        <div>
                          <span className="text-gray-400">Price:</span> {item.unitPrice}
                        </div>
                      </div>
                      <div className="border-t border-gray-100" />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditOffer(item)}
                          className="flex-1 text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteOffer(item.id)}
                          className="text-xs px-3 py-1.5 rounded-md border border-red-200 hover:bg-red-50 text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              renderTable(offers, offerColumns, "No offers found", isLoading)
            )}

            {/* Pagination for Offers */}
            {offerTotalCount > 0 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  className="pagination-bar"
                  currentPage={offerCurrentPage}
                  totalCount={offerTotalCount}
                  pageSize={offerPageSize}
                  siblingCount={1}
                  onPageChange={({ selected }) => {
                    const page = parseInt(selected, 10);
                    if (!isNaN(page) && page > 0) {
                      setOfferCurrentPage(page);
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Currencies Tab */}
        {activeTab === "currencies" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h5 className="text-lg font-semibold">Currencies</h5>
              <IconWordResponsiveButton
                icon={<FaPlus />}
                text="Create Currency"
                onClick={() => {
                  setEditingCurrency(null);
                  setNewCurrency({
                    code: "",
                    name: "",
                    symbol: "",
                    active: true,
                    isDefault: false,
                  });
                  setShowCreateCurrencyModal(true);
                }}
              />
            </div>

            {renderTable(
              currencies,
              [
                {
                  key: "code",
                  header: "Code",
                  render: item => <div className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">{item.code}</div>,
                },
                {
                  key: "name",
                  header: "Name",
                  render: item => (
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-500">{item.symbol}</div>
                    </div>
                  ),
                },
                {
                  key: "isDefault",
                  header: "Default",
                  render: item => (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${item.isDefault ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                    >
                      {item.isDefault ? "Yes" : "No"}
                    </span>
                  ),
                },
                {
                  key: "active",
                  header: "Status",
                  render: item => (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${item.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                    >
                      {item.active ? "Active" : "Inactive"}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  header: "Actions",
                  render: item => (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditCurrency(item)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteCurrency(item)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  ),
                },
              ],
              "No currencies found",
              isLoading
            )}

            {/* Pagination for Currencies */}
            {currencyTotalCount > 0 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  className="pagination-bar"
                  currentPage={currencyCurrentPage}
                  totalCount={currencyTotalCount}
                  pageSize={currencyPageSize}
                  siblingCount={1}
                  onPageChange={({ selected }) => {
                    const page = parseInt(selected, 10);
                    if (!isNaN(page) && page > 0) {
                      setCurrencyCurrentPage(page);
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Discount Modal */}
        <ReusableFormModal
          isOpen={showCreateDiscountModal}
          onClose={() => setShowCreateDiscountModal(false)}
          title={editingDiscount ? "Edit Discount" : "Create New Discount"}
          onSubmit={editingDiscount ? handleUpdateDiscount : handleCreateDiscount}
          submitText={editingDiscount ? "Update Discount" : "Create Discount"}
          maxWidth="sm:max-w-2xl"
          isSubmitting={isLoading}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Code *</label>
              <input
                type="text"
                value={newDiscount.code}
                onChange={e => handleDiscountInputChange("code", e.target.value)}
                placeholder="SAVE20"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Name *</label>
              <input
                type="text"
                value={newDiscount.name}
                onChange={e => handleDiscountInputChange("name", e.target.value)}
                placeholder="Summer Sale"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Description *</label>
            <textarea
              rows={3}
              value={newDiscount.description}
              onChange={e => handleDiscountInputChange("description", e.target.value)}
              placeholder="20% off for summer promotion"
              required
              className="p-2 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Provider Organization Selection */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h6 className="font-medium text-blue-900 mb-2">Provider Organization Selection</h6>
            <p className="text-sm text-blue-700 mb-3">
              Search for an organization to set as the provider. If no provider is selected, the discount will be
              unlinked and available to all organizations.
            </p>

            <div className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  value={organizationSearchTerm}
                  onChange={e => {
                    setOrganizationSearchTerm(e.target.value);
                    if (e.target.value.length >= 2) {
                      searchOrganizations(e.target.value);
                    } else {
                      setSearchResults([]);
                    }
                  }}
                  placeholder="Search by organization name or code..."
                  className="w-full pl-10 pr-4 py-2 bg-white rounded-md border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-4 h-4" />
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-blue-200 rounded-md bg-white">
                  <div className="p-2 text-xs text-gray-500 border-b border-blue-100">
                    Found {searchResults.length} organizations
                  </div>
                  {searchResults.map(organization => (
                    <div
                      key={organization.id}
                      onClick={() => handleOrganizationSelection(organization)}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-blue-100 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FaUser className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{organization.name}</div>
                          <div className="text-xs text-gray-600">
                            {organization.code} • {organization.contactEmail || "No email"}
                          </div>
                          <div className="text-xs text-gray-500">{organization.type?.name || "Unknown Type"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isSearching && <div className="mt-2 text-sm text-blue-600">Searching organizations...</div>}
            </div>

            {/* Selected Organization Display */}
            {selectedOrganization && (
              <div className="p-3 bg-blue-100 rounded-md border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <FaUser className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-blue-900">{selectedOrganization.name}</div>
                      <div className="text-xs text-blue-700">
                        Code: {selectedOrganization.code} (ID: {selectedOrganization.id})
                      </div>
                      <div className="text-xs text-blue-600 mt-1">
                        {selectedProviderId
                          ? `Will create discount for organization ${selectedProviderId}`
                          : "Will create unlinked discount available to all"}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedOrganization(null);
                      setSelectedProviderId(null);
                      setNewDiscount(prev => ({
                        ...prev,
                        organizationId: null,
                      }));
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Provider Status Display */}
          {!selectedOrganization && (
            <div className="p-3 bg-gray-100 rounded-md border border-gray-200">
              <div className="text-sm text-gray-700">
                <strong>No provider selected.</strong> This discount will be unlinked and available to all
                organizations.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Percentage (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={newDiscount.percentage || ""}
                onChange={e => {
                  handleDiscountInputChange("percentage", e.target.value ? parseInt(e.target.value) : null);
                  if (e.target.value) handleDiscountInputChange("fixedValue", null);
                }}
                placeholder="20"
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Fixed Value ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newDiscount.fixedValue || ""}
                onChange={e => {
                  handleDiscountInputChange("fixedValue", e.target.value ? parseFloat(e.target.value) : null);
                  if (e.target.value) handleDiscountInputChange("percentage", null);
                }}
                placeholder="50.00"
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={newDiscount.startDate}
                onChange={e => handleDiscountInputChange("startDate", e.target.value)}
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={newDiscount.endDate}
                onChange={e => handleDiscountInputChange("endDate", e.target.value)}
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newDiscount.active}
                onChange={e => handleDiscountInputChange("active", e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
          </div>
        </ReusableFormModal>

        {/* Create/Edit Offer Modal */}
        {/* Create Currency Modal */}
        <ReusableFormModal
          isOpen={showCreateCurrencyModal}
          onClose={() => setShowCreateCurrencyModal(false)}
          title={editingCurrency ? "Edit Currency" : "Create Currency"}
          onSubmit={editingCurrency ? handleUpdateCurrency : handleCreateCurrency}
          submitText={editingCurrency ? "Update Currency" : "Create Currency"}
          maxWidth="sm:max-w-md"
          isSubmitting={isLoading}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Code *</label>
              <input
                type="text"
                value={newCurrency.code}
                onChange={e =>
                  setNewCurrency(prev => ({
                    ...prev,
                    code: e.target.value.toUpperCase(),
                  }))
                }
                placeholder="USD"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Symbol *</label>
              <input
                type="text"
                value={newCurrency.symbol}
                onChange={e =>
                  setNewCurrency(prev => ({
                    ...prev,
                    symbol: e.target.value,
                  }))
                }
                placeholder="$"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              value={newCurrency.name}
              onChange={e => setNewCurrency(prev => ({ ...prev, name: e.target.value }))}
              placeholder="US Dollar"
              required
              className="p-2 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={newCurrency.active}
                onChange={e =>
                  setNewCurrency(prev => ({
                    ...prev,
                    active: e.target.checked,
                  }))
                }
                className="mr-2"
              />
              Active
            </label>
            <label className="flex items-center text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={newCurrency.isDefault}
                onChange={e =>
                  setNewCurrency(prev => ({
                    ...prev,
                    isDefault: e.target.checked,
                  }))
                }
                className="mr-2"
              />
              Default
            </label>
          </div>
        </ReusableFormModal>

        {/* Confirm Delete Currency */}
        <HConfirmDialog
          show={showDeleteCurrencyDialog}
          onClose={() => {
            setShowDeleteCurrencyDialog(false);
            setCurrencyToDelete(null);
          }}
          onConfirm={confirmDeleteCurrency}
          isLoading={isLoading}
          title="Delete Currency"
          message={`Are you sure you want to delete the currency "${currencyToDelete?.code || "this currency"}"? This action cannot be undone.`}
          confirmText="Delete Currency"
          cancelText="Cancel"
          confirmVariant="destructive"
        />
        <ReusableFormModal
          isOpen={showCreateOfferModal}
          onClose={() => {
            setShowCreateOfferModal(false);
          }}
          title={editingOffer ? "Edit Offer" : "Create New Offer"}
          onSubmit={editingOffer ? handleUpdateOffer : handleCreateOffer}
          submitText={editingOffer ? "Update Offer" : "Create Offer"}
          maxWidth="max-w-3xl w-[90vw]"
          isLoading={isLoadingOfferDetails}
          loadingText="Loading offer details..."
          isSubmitting={isLoading || isLoadingOfferDetails}
        >
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Code *</label>
              <input
                type="text"
                value={newOffer.code}
                onChange={e => handleOfferInputChange("code", e.target.value)}
                placeholder="PREMIUM-WORKSPACE"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Name *</label>
              <input
                type="text"
                value={newOffer.name}
                onChange={e => handleOfferInputChange("name", e.target.value)}
                placeholder="Premium Workspace Package"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Description *</label>
            <textarea
              rows={3}
              value={newOffer.description}
              onChange={e => handleOfferInputChange("description", e.target.value)}
              placeholder="High-end workspace with all amenities"
              required
              className="p-2 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Provider Organization Selection */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h6 className="font-medium text-blue-900 mb-2">Provider Organization Selection</h6>
            <p className="text-sm text-blue-700 mb-3">
              Search for an organization to set as the provider. If no provider is selected, the offer will be unlinked
              and available to all organizations.
            </p>

            <div className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  value={organizationSearchTerm}
                  onChange={e => {
                    setOrganizationSearchTerm(e.target.value);
                    if (e.target.value.length >= 2) {
                      searchOrganizations(e.target.value);
                    } else {
                      setSearchResults([]);
                    }
                  }}
                  placeholder="Search by organization name or code..."
                  className="w-full pl-10 pr-4 py-2 bg-white rounded-md border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-4 h-4" />
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-blue-200 rounded-md bg-white">
                  <div className="p-2 text-xs text-gray-500 border-b border-blue-100">
                    Found {searchResults.length} organizations
                  </div>
                  {searchResults.map(organization => (
                    <div
                      key={organization.id}
                      onClick={() => handleOrganizationSelection(organization)}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b border-blue-100 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FaUser className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{organization.name}</div>
                          <div className="text-xs text-gray-600">
                            {organization.code} • {organization.contactEmail || "No email"}
                          </div>
                          <div className="text-xs text-gray-500">{organization.type?.name || "Unknown Type"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isSearching && <div className="mt-2 text-sm text-blue-600">Searching organizations...</div>}
            </div>

            {/* Selected Organization Display */}
            {selectedOrganization && (
              <div className="p-3 bg-blue-100 rounded-md border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <FaUser className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-blue-900">{selectedOrganization.name}</div>
                      <div className="text-xs text-blue-700">
                        Code: {selectedOrganization.code} (ID: {selectedOrganization.id})
                      </div>
                      <div className="text-xs text-blue-600 mt-1">
                        {selectedProviderId
                          ? `Will create offer for organization ${selectedProviderId}`
                          : "Will create unlinked offer available to all"}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedOrganization(null);
                      setSelectedProviderId(null);
                      setNewOffer(prev => ({
                        ...prev,
                        organizationId: null,
                      }));
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* Organization Selection is optional - no error display needed */}
          </div>

          {/* Provider Status Display */}
          {!selectedOrganization && (
            <div className="p-3 bg-gray-100 rounded-md border border-gray-200">
              <div className="text-sm text-gray-700">
                <strong>No provider selected.</strong> This offer will be unlinked and available to all organizations.
              </div>
            </div>
          )}

          {/* Service Category and Subcategory Selection */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Service Category *</label>
              <select
                value={_selectedCategoryId || ""}
                onChange={e => {
                  const categoryId = e.target.value ? parseInt(e.target.value) : null;
                  setSelectedCategoryId(categoryId);
                  if (categoryId) {
                    fetchServiceSubcategories(categoryId);
                  } else {
                    setServiceSubcategories([]);
                  }
                  // Clear subcategory selection when category changes
                  handleOfferInputChange("serviceSubcategoryId", null);
                }}
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select a category</option>
                {serviceCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Service Subcategory *</label>
              <select
                value={newOffer.serviceSubcategoryId || ""}
                onChange={e =>
                  handleOfferInputChange("serviceSubcategoryId", e.target.value ? parseInt(e.target.value) : null)
                }
                required
                disabled={!serviceSubcategories.length}
                className="p-2 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">
                  {serviceSubcategories.length === 0
                    ? _selectedCategoryId
                      ? "Loading subcategories..."
                      : "Select a category first"
                    : "Select a subcategory"}
                </option>
                {serviceSubcategories.map(subcategory => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name} ({subcategory.code})
                  </option>
                ))}
              </select>
              {_selectedCategoryId && serviceSubcategories.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">No subcategories found for this category</p>
              )}
              {/* Service Subcategory Error */}
              {offerFormErrors.serviceSubcategoryId && (
                <div className="mt-1 text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
                  {offerFormErrors.serviceSubcategoryId}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block mb-3 text-sm font-medium text-gray-700">Packages (Optional)</label>

            {packages.length === 0 ? (
              <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-md border border-gray-200">
                Loading packages...
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                  {packages.map(pkg => (
                    <div key={pkg.id} className="flex items-start space-x-3">
                      <Checkbox
                        id={`package-${pkg.id}`}
                        checked={newOffer.packageIds.includes(pkg.id)}
                        onCheckedChange={checked => {
                          const pkgProviderId = pkg.providerId || pkg.provider?.id || null;
                          if (checked) {
                            // If no provider selected yet and package has provider, set provider and refilter packages
                            if (!selectedProviderId && pkgProviderId) {
                              setSelectedProviderId(pkgProviderId);
                              setNewOffer(prev => ({ ...prev, providerId: pkgProviderId }));
                              // Refresh packages to show only those for this provider
                              setPackageCurrentPage(1);
                              fetchPackages(1);
                            }
                            // Add package to selection
                            handleOfferInputChange("packageIds", [...newOffer.packageIds, pkg.id]);
                          } else {
                            // Remove package from selection
                            handleOfferInputChange(
                              "packageIds",
                              newOffer.packageIds.filter(id => id !== pkg.id)
                            );
                          }
                        }}
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={`package-${pkg.id}`}
                          className="text-sm font-medium text-gray-900 cursor-pointer"
                        >
                          {pkg.name || pkg.code || pkg.id}
                        </label>
                        <p className="text-xs text-gray-600 mt-1">{pkg.description || "No description"}</p>
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2">
                            {pkg.type}
                          </span>
                          <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded">
                            ${pkg.amount}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {packageTotalCount > packagePageSize && (
                  <div className="mt-3 flex justify-center">
                    <Pagination
                      currentPage={packageCurrentPage}
                      totalCount={packageTotalCount}
                      pageSize={packagePageSize}
                      onPageChange={({ selected }) => {
                        setPackageCurrentPage(selected);
                        fetchPackages(selected);
                      }}
                    />
                  </div>
                )}
              </>
            )}

            <p className="mt-2 text-xs text-gray-500">
              Select the packages that should be associated with this offer. Leave empty if no packages are associated.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Service Tier *</label>
              <select
                value={newOffer.serviceTier}
                onChange={e => handleOfferInputChange("serviceTier", e.target.value)}
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Currency *</label>
              <select
                value={newOffer.currencyId || ""}
                onChange={e => handleOfferInputChange("currencyId", parseInt(e.target.value))}
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select currency</option>
                {currencies.map(currency => (
                  <option key={currency.id} value={currency.id}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </option>
                ))}
              </select>
              {offerFormErrors.currencyId && <p className="text-xs text-red-600 mt-1">{offerFormErrors.currencyId}</p>}
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Unit Price *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newOffer.unitPrice}
                onChange={e => handleOfferInputChange("unitPrice", parseFloat(e.target.value))}
                required
                placeholder="100.00"
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {offerFormErrors.unitPrice && <p className="text-xs text-red-600 mt-1">{offerFormErrors.unitPrice}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Duration Unit *</label>
              <select
                value={newOffer.durationUnit}
                onChange={e => handleOfferInputChange("durationUnit", e.target.value)}
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="HOUR">Hour</option>
                <option value="DAY">Day</option>
                <option value="MONTH">Month</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Min Duration *</label>
              <input
                type="number"
                min="1"
                value={newOffer.minimumDuration}
                onChange={e => handleOfferInputChange("minimumDuration", parseInt(e.target.value))}
                placeholder="1"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Max Duration</label>
              <input
                type="number"
                min="1"
                value={newOffer.maximumDuration || ""}
                onChange={e =>
                  handleOfferInputChange("maximumDuration", e.target.value ? parseInt(e.target.value) : null)
                }
                placeholder="8"
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Max Check-ins *</label>
              <input
                type="number"
                min="1"
                value={newOffer.maximumCheckIns || ""}
                onChange={e =>
                  handleOfferInputChange("maximumCheckIns", e.target.value ? parseInt(e.target.value) : null)
                }
                placeholder="5"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {offerFormErrors.maximumCheckIns && (
                <p className="text-xs text-red-600 mt-1">{offerFormErrors.maximumCheckIns}</p>
              )}
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-700">Number of Service Providers *</label>
              <input
                type="number"
                min="1"
                value={newOffer.numberOfServiceProviders}
                onChange={e => handleOfferInputChange("numberOfServiceProviders", parseInt(e.target.value))}
                placeholder="2"
                required
                className="p-1.5 w-full bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </ReusableFormModal>

        {/* Confirm Delete Dialogs */}
        <HConfirmDialog
          show={showDeleteDiscountDialog}
          onClose={() => {
            setShowDeleteDiscountDialog(false);
            setDiscountToDelete(null);
          }}
          onConfirm={confirmDeleteDiscount}
          isLoading={isLoading}
          title="Delete Discount"
          message={`Are you sure you want to delete the discount "${discountToDelete?.name || "this discount"}"? This action cannot be undone.`}
          confirmText="Delete Discount"
          cancelText="Cancel"
          confirmVariant="destructive"
        />

        <HConfirmDialog
          show={showDeleteOfferDialog}
          onClose={() => {
            setShowDeleteOfferDialog(false);
            setOfferToDelete(null);
          }}
          onConfirm={confirmDeleteOffer}
          isLoading={isLoading}
          title="Delete Offer"
          message={`Are you sure you want to delete the offer "${offerToDelete?.name || "this offer"}"? This action cannot be undone.`}
          confirmText="Delete Offer"
          cancelText="Cancel"
          confirmVariant="destructive"
        />
      </div>
    </TablePageLayout>
  );
};

export default OffersDiscounts;
